import { assetIdLikeEquals, Builder, Parser } from "@repo/caip";
import { resolveCryptoAssetRouteChain } from "~/lib/crypto-asset-route";
import type { SymbolTradeRouteOption } from "../symbol-trade-routes";

const STOCK_TRADE_SLUG_PATTERN = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/u;
const STOCK_TRADE_SLUG_MAX_LENGTH = 128;
const STOCK_TRADE_ASSET_ID_MAX_LENGTH = 256;

export interface StockTradeSearch {
  readonly assetId?: string | null;
  readonly chain?: string | null;
  readonly issuer?: string | null;
  readonly product?: string | null;
}

export interface StockTradeSearchUpdate {
  readonly assetId: string | undefined;
  readonly chain: string | undefined;
  readonly issuer: string | undefined;
  readonly product: string | undefined;
}

export type StockTradeSearchResolution =
  | { readonly kind: "default" }
  | { readonly kind: "invalid" }
  | { readonly kind: "unavailable" }
  | { readonly kind: "ambiguous" }
  | {
      readonly kind: "selected";
      readonly option: SymbolTradeRouteOption;
    };

interface NormalizedSearchValue {
  readonly present: boolean;
  readonly value: string | null | undefined;
}

function hasOwnSearchKey(raw: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(raw, key);
}

function normalizeSlug(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 &&
    normalized.length <= STOCK_TRADE_SLUG_MAX_LENGTH &&
    STOCK_TRADE_SLUG_PATTERN.test(normalized)
    ? normalized
    : null;
}

function normalizeAssetId(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  if (
    normalized.length === 0 ||
    normalized.length > STOCK_TRADE_ASSET_ID_MAX_LENGTH
  ) {
    return null;
  }

  try {
    const parsed = Parser.parseAssetId(normalized);
    return Builder.buildAssetId(parsed.chain, parsed.asset);
  } catch {
    return null;
  }
}

function readSearchValue(
  raw: Record<string, unknown>,
  key: string,
  normalize: (value: unknown) => string | null,
): NormalizedSearchValue {
  // A key that is present with an `undefined` value is absence, not an
  // invalid value: the route's functional search updater spreads
  // `{ assetId: undefined, product: undefined }` to clear the selectors it is
  // not using, and the validator runs on that object before serialization.
  // Treating it as invalid wrote `assetId=null` into the URL and turned every
  // route change on the symbol page into an unresolvable deep link.
  if (!hasOwnSearchKey(raw, key) || raw[key] === undefined) {
    return { present: false, value: undefined };
  }

  return { present: true, value: normalize(raw[key]) };
}

function normalizeChain(value: unknown): string | null {
  const slugOrChainId = normalizeSlug(value);
  if (!slugOrChainId) {
    return null;
  }

  return resolveCryptoAssetRouteChain(slugOrChainId)?.slug ?? null;
}

/**
 * Search validation keeps explicit invalid values as null so a malformed or
 * duplicated deep link cannot collapse into the route's ordinary default.
 */
export function normalizeStockTradeSearch(
  raw: Record<string, unknown>,
): StockTradeSearch {
  const assetId = readSearchValue(raw, "assetId", normalizeAssetId);
  const chain = readSearchValue(raw, "chain", normalizeChain);
  const issuer = readSearchValue(raw, "issuer", normalizeSlug);
  const product = readSearchValue(raw, "product", normalizeSlug);

  return {
    ...(assetId.present ? { assetId: assetId.value ?? null } : {}),
    ...(chain.present ? { chain: chain.value ?? null } : {}),
    ...(issuer.present ? { issuer: issuer.value ?? null } : {}),
    ...(product.present ? { product: product.value ?? null } : {}),
  };
}

export function clearStockTradeSearch(): StockTradeSearchUpdate {
  return {
    assetId: undefined,
    chain: undefined,
    issuer: undefined,
    product: undefined,
  };
}

function hasRequestedStockTradeSearch(search: StockTradeSearch): boolean {
  return (
    search.assetId !== undefined ||
    search.chain !== undefined ||
    search.issuer !== undefined ||
    search.product !== undefined
  );
}

export function resolveStockTradeSearch(
  options: readonly SymbolTradeRouteOption[],
  search: StockTradeSearch,
): StockTradeSearchResolution {
  if (!hasRequestedStockTradeSearch(search)) {
    return { kind: "default" };
  }

  if (
    search.assetId === null ||
    search.chain === null ||
    search.issuer === null ||
    search.product === null
  ) {
    return { kind: "invalid" };
  }

  const requestedChain =
    search.chain === undefined
      ? null
      : resolveCryptoAssetRouteChain(search.chain);
  if (search.chain !== undefined && requestedChain === null) {
    return { kind: "invalid" };
  }

  const requestedAssetId = search.assetId;
  const requestedIssuer = search.issuer;
  const requestedProduct = search.product;

  const matches = options.filter((option) => {
    if (
      requestedAssetId !== undefined &&
      !assetIdLikeEquals(option.assetId, requestedAssetId)
    ) {
      return false;
    }
    if (
      requestedChain !== null &&
      String(option.chainId) !== requestedChain.chainId
    ) {
      return false;
    }
    if (
      requestedIssuer !== undefined &&
      normalizeSlug(option.product?.issuerSlug) !== requestedIssuer
    ) {
      return false;
    }
    if (
      requestedProduct !== undefined &&
      normalizeSlug(option.product?.productSlug) !== requestedProduct
    ) {
      return false;
    }
    return true;
  });

  if (matches.length === 0) {
    return { kind: "unavailable" };
  }
  const selectsFirstRankedChainMatch =
    requestedChain !== null &&
    requestedAssetId === undefined &&
    requestedIssuer === undefined &&
    requestedProduct === undefined;
  if (matches.length > 1 && !selectsFirstRankedChainMatch) {
    return { kind: "ambiguous" };
  }

  // SymbolPage supplies ranked options, so a chain-only deep link inherits the
  // same deterministic first-row preference shown in Advanced Settings.
  const option = matches[0];
  return option ? { kind: "selected", option } : { kind: "unavailable" };
}

function selectionMatchesOption(
  resolution: StockTradeSearchResolution,
  option: SymbolTradeRouteOption,
): boolean {
  return resolution.kind === "selected" && resolution.option.id === option.id;
}

function toSearchUpdate(search: StockTradeSearch): StockTradeSearchUpdate {
  return {
    assetId: search.assetId ?? undefined,
    chain: search.chain ?? undefined,
    issuer: search.issuer ?? undefined,
    product: search.product ?? undefined,
  };
}

/**
 * Prefer the shortest human-readable selector that uniquely identifies the
 * chosen concrete option. CAIP-19 remains the exact fallback for routes that
 * lack stable issuer/product metadata or remain ambiguous.
 */
export function buildStockTradeSearchForOption(
  option: SymbolTradeRouteOption,
  options: readonly SymbolTradeRouteOption[],
): StockTradeSearchUpdate | null {
  const chain = resolveCryptoAssetRouteChain(option.chainId)?.slug ?? null;
  const issuer = normalizeSlug(option.product?.issuerSlug);
  if (chain && issuer) {
    const humanSearch = { chain, issuer } satisfies StockTradeSearch;
    if (
      selectionMatchesOption(
        resolveStockTradeSearch(options, humanSearch),
        option,
      )
    ) {
      return toSearchUpdate(humanSearch);
    }

    const product = normalizeSlug(option.product?.productSlug);
    if (product) {
      const productSearch = {
        ...humanSearch,
        product,
      } satisfies StockTradeSearch;
      if (
        selectionMatchesOption(
          resolveStockTradeSearch(options, productSearch),
          option,
        )
      ) {
        return toSearchUpdate(productSearch);
      }
    }
  }

  const assetId = normalizeAssetId(option.assetId);
  return assetId
    ? toSearchUpdate({ assetId } satisfies StockTradeSearch)
    : null;
}
